import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { FONTS } from '../../../constants/config';
import { createShadow } from '../../../utils/styles';
import { Toast } from '../../../components';
import { SpecializationKPI } from '../types';

const FOREGROUND = '#414042';

interface KPIsSectionProps {
  kpis: SpecializationKPI[];
  onViewKPI?: (kpiId: number) => void;
  onAddKPI?: () => void;
  readOnly?: boolean;
}

function KPICard({ kpi, onView }: { kpi: SpecializationKPI; onView?: () => void }) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const achievedColor = kpi.isAchieved ? '#2ca02c' : '#ff7f0e';
  const unitLabel = kpi.unit === 'percentage' ? '%' : (isRTL ? 'عدد' : 'count');

  return (
    <View
      style={[
        cardStyles.container,
        { borderColor: '#e2e8f0' },
      ]}
    >
      {/* Title */}
      <View style={cardStyles.titleWrap}>
        <Text style={[cardStyles.title, { color: FOREGROUND }]}>
          {kpi.name}
        </Text>
      </View>

      {/* Target + Achieved boxes */}
      <View style={cardStyles.metricsRow}>
        {/* Achieved */}
        <View style={cardStyles.metricBox}>
          <Text style={[cardStyles.metricLabel, { color: FOREGROUND }]}>
            {isRTL ? 'المحقق' : 'Achieved'}
          </Text>
          <View style={cardStyles.metricValueRow}>
            <Text style={[cardStyles.metricUnit, { color: achievedColor }]}>{unitLabel}</Text>
            <Text style={[cardStyles.metricValue, { color: achievedColor }]}>
              {kpi.achievedValue}
            </Text>
          </View>
        </View>

        {/* Target */}
        <View style={cardStyles.metricBox}>
          <Text style={[cardStyles.metricLabel, { color: FOREGROUND }]}>
            {isRTL ? 'المستهدف' : 'Target'}
          </Text>
          <View style={cardStyles.metricValueRow}>
            <Text style={[cardStyles.metricUnit, { color: FOREGROUND }]}>{unitLabel}</Text>
            <Text style={[cardStyles.metricValue, { color: FOREGROUND }]}>
              {kpi.targetValue}
            </Text>
          </View>
        </View>
      </View>

      {/* View Details link */}
      <TouchableOpacity style={cardStyles.linkRow} onPress={onView}>
        <Ionicons name="chevron-back" size={16} color={FOREGROUND} />
        <Text style={[cardStyles.linkText, { color: FOREGROUND }]}>
          {isRTL ? 'عرض التفاصيل' : 'View Details'}
        </Text>
      </TouchableOpacity>
    </View>
  );
}

export default function KPIsSection({ kpis, onViewKPI, onAddKPI, readOnly }: KPIsSectionProps) {
  const { colors } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [toastVisible, setToastVisible] = useState(false);

  const handleAddKPI = () => {
    if (onAddKPI) { onAddKPI(); return; }
    setToastVisible(true);
  };

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: '#FFFFFF',
          borderColor: '#e2e8f0',
        },
        createShadow(0, 1, 2, '#000', 0.1),
      ]}
    >
      {/* Header */}
      <View style={styles.header}>
        {!readOnly && (
          <TouchableOpacity
            style={[styles.addBtn, createShadow(0, 1, 3, '#000', 0.1)]}
            onPress={handleAddKPI}
          >
            <Text style={[styles.addBtnText, { color: FOREGROUND }]}>
              {isRTL ? 'إضافة مؤشر' : 'Add KPI'}
            </Text>
            <Ionicons name="add-circle-outline" size={14} color={FOREGROUND} />
          </TouchableOpacity>
        )}
        <Text style={[styles.sectionTitle, { color: FOREGROUND }]}>
          {isRTL ? 'مؤشرات الأداء' : 'Performance Indicators'}
        </Text>
      </View>

      {/* KPI Cards — Figma uses a 4-column grid: 2 visible cards + 2 empty spacers */}
      <View style={[styles.cardsRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
        {kpis.map((kpi) => (
          <KPICard key={kpi.id} kpi={kpi} onView={() => onViewKPI?.(kpi.linkId || kpi.id)} />
        ))}
        <View style={styles.cardSpacer} />
        <View style={styles.cardSpacer} />
      </View>
      <Toast visible={toastVisible} message={isRTL ? 'قريباً' : 'Coming soon'} onHide={() => setToastVisible(false)} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    width: '100%',
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
    flex: 1,
    lineHeight: 20,
  },
  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    minHeight: 32,
  },
  addBtnText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'center',
  },
  cardsRow: {
    gap: 20,
    borderRadius: 8,
    padding: 12,
    backgroundColor: '#f1f5f9',
    flexWrap: 'wrap',
  },
  cardSpacer: {
    flex: 1,
    minWidth: 200,
    maxWidth: 320,
  },
});

const cardStyles = StyleSheet.create({
  container: {
    flex: 1,
    minWidth: 200,
    maxWidth: 320,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    gap: 8,
    alignSelf: 'stretch',
  },
  titleWrap: {
    width: '100%',
  },
  title: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
    lineHeight: 20,
    color: FOREGROUND,
  },
  metricsRow: {
    flexDirection: 'row',
    gap: 8,
    width: '100%',
  },
  metricBox: {
    flex: 1,
    backgroundColor: '#f1f5f9',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
    gap: 4,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  metricLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    textAlign: 'right',
    width: '100%',
    lineHeight: 14,
  },
  metricValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  metricUnit: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    lineHeight: 14,
  },
  metricValue: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    fontWeight: '600',
    textAlign: 'right',
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    minHeight: 24,
  },
  linkText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    textAlign: 'right',
    lineHeight: 14,
  },
});
